import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import moment from 'moment-timezone'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableHead, TableBody, TableRow, TableCell } from 'transfix-ui/components/Table'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { $enum } from 'ts-enum-util'
import { EDI_FILE_TYPE_NAMES } from 'apps/edi-tools/components/FilesTable/Filters'
import { IEdiFile } from 'apps/edi-tools/types'
import { InfoIcon } from 'transfix-icons'

const EdiFilesTableHeader = () => {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Created</TableCell>
        <TableCell>EDI Message Type</TableCell>
        <TableCell>Shipper</TableCell>
        <TableCell>Shipment</TableCell>
        <TableCell>
          Shipment Status
          <Tooltip
            effect='float'
            id='shipmentStatusInfo'
            position='bottom'
            trigger={
              <a aria-label='Shipment Status Info' href='/edi-tools'>
                <InfoIcon />
              </a>
            }
            type='dark'>
            <p>
              Shipment Status was the status of the shipment <br />
              at the the time the EDI file was created,
              <br /> the current status of the shipment could be different.
            </p>
          </Tooltip>
        </TableCell>
        <TableCell>EDI File</TableCell>
        <TableCell>Processed</TableCell>
      </TableRow>
    </TableHead>
  )
}

interface IPropsEdiFileRow {
  ediFile: IEdiFile
}

const EdiFilesTableRow = ({ ediFile }: IPropsEdiFileRow) => {
  const { pathname = '' } = useLocation()
  return (
    <TableRow key={ediFile.id}>
      <TableCell>
        {/* TODO: check & adjust parsing to account for timezone */}
        {moment(ediFile.createdAt).fromNow()}{' '}
        <p className='text-small'>
          ({moment(ediFile.createdAt).format('MMMM Do YYYY, h:mm:ss A')})
        </p>
      </TableCell>
      <TableCell>{$enum(EDI_FILE_TYPE_NAMES).getKeyOrDefault(ediFile.ediFileType, '')}</TableCell>
      <TableCell>
        <a href={`/shippers/${ediFile.shipperApplicationId}`}>{ediFile.shipperApplicationId}</a>
      </TableCell>
      <TableCell>
        <a href={`/shipments/${ediFile.shipmentId}`}>{ediFile.shipmentId}</a>
      </TableCell>
      <TableCell>{ediFile.shipmentStatus}</TableCell>
      <TableCell>
        <Link to={`${pathname}${ediFile.id}/details`}>Details</Link>
      </TableCell>
      <TableCell>
        {/* TODO: check & adjust parsing to account for timezone & missing values */}
        {moment(ediFile.processedAt).fromNow()}{' '}
        <p className='text-small'>
          ({moment(ediFile.processedAt).format('MMMM Do YYYY, h:mm:ss A')})
        </p>
      </TableCell>
    </TableRow>
  )
}

interface IProps {
  ediFiles: IEdiFile[]
  currentPage: number
  hasNextPage: boolean
  onPageChange: (pageNumber: number) => void
  loading: boolean
  loadingError: boolean
}

export const EdiFilesTable = ({
  currentPage,
  ediFiles,
  hasNextPage,
  loading,
  loadingError,
  onPageChange,
}: IProps) => (
  <div>
    {!loading && !ediFiles.length && (
      <InlineMessage
        message='There are no EDI files matching the criteria.'
        type={INLINE_MESSAGE_VARIANTS.INFO}
      />
    )}
    {!loading && loadingError && (
      <InlineMessage
        message='There was an error loading EDI files.'
        type={INLINE_MESSAGE_VARIANTS.INFO}
      />
    )}
    <Table>
      <EdiFilesTableHeader />
      <TableBody>
        {loading ? (
          <Spinner position='relative' />
        ) : (
          ediFiles.map(ediFile => <EdiFilesTableRow key={ediFile.id} ediFile={ediFile} />)
        )}
      </TableBody>
    </Table>
    {/* TODO: handle the number of pages issue - can't tell from API */}
    {!(currentPage === 1 && !hasNextPage) && (
      <Pagination currentPage={currentPage} numberOfPages={100} onPageChange={onPageChange} />
    )}
  </div>
)
